import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TRANSFER_FEE_USD, parseXrpDestinationTag, supportsMemo } from '@incpt/shared';
import { isValidAddress, preloadTronWeb } from '@incpt/blockchain';
import { addressDisplayName } from '../../lib/walletNames';
import { ScreenBackdrop } from './ScreenBackdrop';
import { PILL_INPUT } from './inputStyles';
import { Numpad, applyAmountKey, type NumpadKey } from './Numpad';
import { BottomSheet } from './BottomSheet';
import { AddressSheet } from './AddressSheet';
import { NetworkSelect } from './NetworkSelect';
import { DEFAULT_COIN, SENDABLE_COINS, type Coin } from './TokenSheet';
import { ProgressButton } from './ProgressButton';
import { Confetti } from './Confetti';
import { DiagonalArrow, ChevronRight, WarningIcon, ScanIcon } from './icons';
import { QrScanOverlay } from './QrScanOverlay';
import { useBackButton, hasTelegramBackButton } from '../../hooks/useBackButton';
import { useWallet } from '../../hooks/useWallet';
import { useTransfers } from '../../hooks/useTransfers';
import { useFeeQuote } from '../../hooks/useFeeQuote';
import { useBtcSpendQuote } from '../../hooks/useBtcSpendQuote';
import { useMe } from '../../hooks/useMe';
import { useResolveRecipient } from '../../hooks/useResolveRecipient';
import { ReauthFlow } from './ReauthFlow';
import { formatTokenAmount, formatUsd, DISPLAY_DECIMALS } from '../../lib/format';
import { parseTelegramId } from '../../lib/recipients';
import { shortenAddress } from '../../lib/walletNames';
import { dbg } from '../../lib/debug';

/** Optional prefill — used by the history "Resend" action to reopen this screen
 * with the same recipient and amount. */
export interface SendPrefill {
  to?: string;
  amount?: string;
}

export function SendScreen({
  coin = DEFAULT_COIN,
  onClose,
  prefill,
}: {
  coin?: Coin;
  onClose: () => void;
  prefill?: SendPrefill;
}) {
  const { t } = useTranslation();
  const [scanOpen, setScanOpen] = useState(false);
  // Use Telegram's native back button; fall back to our in-page one outside TG.
  // When the camera scanner is open, back closes it first.
  useBackButton(() => {
    if (scanOpen) setScanOpen(false);
    else onClose();
  });
  const tgBack = hasTelegramBackButton();
  const { wallet, balances, priceBySymbol } = useWallet();
  const { send } = useTransfers();
  const { me, initData, canFetch } = useMe();
  const walletName = wallet?.name ?? 'Main wallet';

  // Network switcher: user can flip between chains for the same symbol.
  const [activeChain, setActiveChain] = useState(coin.chain);
  const activeCoin =
    SENDABLE_COINS.find((c) => c.symbol === coin.symbol && c.chain === activeChain) ?? coin;
  // Network switcher: only the chains this coin can actually leave on.
  const altCoins = SENDABLE_COINS.filter((c) => c.symbol === coin.symbol);

  const [amount, setAmount] = useState(prefill?.amount ?? '0');
  const [address, setAddress] = useState(prefill?.to ?? '');
  /**
   * Destination memo / tag, on the chains that have one.
   *
   * Optional by design: it is needed when paying a SHARED address — an
   * exchange, where one address serves every customer and the tag says which
   * one. Paying a personal wallet needs none, and our own receiving addresses
   * never do, so requiring it would block the common case to serve the rare.
   */
  const [memo, setMemo] = useState('');
  // Address+Send follows the keyboard. Freezing the pre-focus Y left a band
  // of page background between the button and the keys.
  const [addressFocused, setAddressFocused] = useState(false);
  const [dockTop, setDockTop] = useState<number | null>(null);
  const [dockFlowHeight, setDockFlowHeight] = useState(0);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const addressDockRef = useRef<HTMLDivElement>(null);
  const addressFocusedRef = useRef(false);
  const restingViewportHeightRef = useRef(0);
  const nativeKeyboardSeenRef = useRef(false);
  /** Tallest keyboard seen this focus — the yardstick for "it is going away". */
  const keyboardPeakRef = useRef(0);
  const [debouncedTelegramId, setDebouncedTelegramId] = useState<string | null>(null);
  const [selectedAddr, setSelectedAddr] = useState<string | null>(null);
  const [walletOpen, setWalletOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reauthOpen, setReauthOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // One idempotency key per send-intent, reused across retries (reauth, a lost
  // response, a re-tap) so the backend dedupes instead of re-renting energy /
  // re-broadcasting. Reset whenever the amount or recipient changes — that's a
  // genuinely new transfer.
  const idempotencyRef = useRef<string | null>(null);
  useEffect(() => {
    idempotencyRef.current = null;
  }, [amount, address]);

  const clearAddressMode = () => {
    addressFocusedRef.current = false;
    nativeKeyboardSeenRef.current = false;
    keyboardPeakRef.current = 0;
    setAddressFocused(false);
    setDockTop(null);
    setDockFlowHeight(0);
  };

  /** Sit the address+send dock on the visible viewport, just above the keys. */
  const stickAddressDock = () => {
    const dock = addressDockRef.current;
    const vv = window.visualViewport;
    if (!dock || !vv || !addressFocusedRef.current) return;
    const rect = dock.getBoundingClientRect();
    const delta = vv.height - 8 - rect.bottom;
    if (Math.abs(delta) < 2) return;
    setDockTop((top) => Math.max(8, Math.round((top ?? rect.top) + delta)));
  };

  // Mobile browsers keep an input focused when the user dismisses the native
  // keyboard with its down/done control, so onBlur never fires. Detect the
  // viewport returning to its resting height and explicitly leave address mode.
  useEffect(() => {
    const viewportHeight = () => window.visualViewport?.height ?? window.innerHeight;
    restingViewportHeightRef.current = viewportHeight();

    const onViewportResize = () => {
      const height = viewportHeight();
      if (!addressFocusedRef.current) {
        restingViewportHeightRef.current = height;
        return;
      }

      const keyboardHeight = Math.max(0, restingViewportHeightRef.current - height);
      if (keyboardHeight > 120) {
        nativeKeyboardSeenRef.current = true;
        keyboardPeakRef.current = Math.max(keyboardPeakRef.current, keyboardHeight);
      }

      // Leave address mode once the keyboard has retracted past half of its
      // OWN height, rather than past a fixed 80px.
      //
      // The thresholds used to be absolutes — stick above 120, leave below 80 —
      // which left the band between them doing nothing at all. A dismissing
      // keyboard passes straight through it, so the dock stayed frozen at its
      // last fixed position while the keyboard slid away: a stranded input and
      // Send button with an empty strip where the numpad belongs, which is
      // what the recording shows. Measuring against the keyboard's own peak
      // also covers the short ones (floating, external accessory bar) that
      // never cleared the old 120px bar at all.
      if (nativeKeyboardSeenRef.current && keyboardHeight * 2 < keyboardPeakRef.current) {
        addressInputRef.current?.blur();
        clearAddressMode();
        return;
      }

      // Still in address mode: follow the visible viewport through every
      // height the keyboard passes on its way in and out, so the dock never
      // sits still while the screen under it moves.
      stickAddressDock();
    };

    const onViewportScroll = () => {
      if (addressFocusedRef.current && nativeKeyboardSeenRef.current) stickAddressDock();
    };
    window.addEventListener('resize', onViewportResize);
    window.visualViewport?.addEventListener('resize', onViewportResize);
    window.visualViewport?.addEventListener('scroll', onViewportScroll);
    return () => {
      window.removeEventListener('resize', onViewportResize);
      window.visualViewport?.removeEventListener('resize', onViewportResize);
      window.visualViewport?.removeEventListener('scroll', onViewportScroll);
    };
  }, []);

  const needsReauth = !!me?.pinSet || !!me?.biometricEnabled;

  // Source address (selected, default = primary for the chain) + its balance.
  const chainAddresses = (wallet?.addresses ?? []).filter((a) => a.chain === activeCoin.chain);
  const sourceIdx = Math.max(
    0,
    chainAddresses.findIndex((a) => a.eoaAddress === selectedAddr),
  );
  const source = chainAddresses[sourceIdx];
  // Same shared naming as the coin detail / address selector.
  const sourceName = source ? addressDisplayName(source, t('nav.wallet')) : walletName;
  const available = balances
    .filter(
      (b) =>
        b.chain === activeCoin.chain &&
        b.token === activeCoin.symbol &&
        b.address === source?.eoaAddress,
    )
    .reduce((sum, b) => sum + Number(b.raw) / 10 ** b.decimals, 0);

  // A recipient can be either a chain address or another IncptWallet user's
  // Telegram ID. IDs resolve to that user's first address on the active chain.
  const recipientInput = address.trim();
  const memoInput = memo.trim();
  const chainTakesMemo = supportsMemo(activeCoin.chain);
  // Shown as an error only once something has been typed — an empty optional
  // field is not a mistake.
  const memoInvalid = (() => {
    if (!chainTakesMemo || memoInput === '') return false;
    try {
      parseXrpDestinationTag(memoInput);
      return false;
    } catch {
      return true;
    }
  })();
  const directAddressValid =
    recipientInput.length > 0 && isValidAddress(activeCoin.chain, recipientInput);
  const telegramId = directAddressValid ? null : parseTelegramId(recipientInput);

  useEffect(() => {
    if (!telegramId) {
      setDebouncedTelegramId(null);
      return;
    }
    const id = window.setTimeout(() => setDebouncedTelegramId(telegramId), 350);
    return () => window.clearTimeout(id);
  }, [telegramId]);

  const resolved = useResolveRecipient(debouncedTelegramId, activeCoin.chain);
  const resolutionMatchesInput =
    !!telegramId &&
    telegramId === debouncedTelegramId &&
    resolved.recipient?.tgUserId === telegramId;
  const recipient = directAddressValid
    ? recipientInput
    : resolutionMatchesInput
      ? (resolved.recipient?.address ?? '')
      : '';
  const recipientValid = recipient.length > 0 && isValidAddress(activeCoin.chain, recipient);
  const resolvingId = !!telegramId && (telegramId !== debouncedTelegramId || resolved.resolving);
  const idLookupFailed = !!telegramId && telegramId === debouncedTelegramId && resolved.failed;
  const badAddress =
    recipientInput.length > 0 && !directAddressValid && (!telegramId || idLookupFailed);

  // Dynamic (cost-plus) fee estimate for this coin. Pass the recipient (once valid)
  // and the source address so the Tron estimate matches the real rental — recipient's
  // USDT slot + the sender's live energy/free bandwidth, not a best-case guess.
  // The static flat fee is only a display placeholder while the quote loads; Send
  // waits for `feeFresh`. The fee returned by the send itself is authoritative.
  const {
    feeUsd: feeQuote,
    maxFeeUsd,
    feeNative,
    fresh: feeFresh,
  } = useFeeQuote(
    activeCoin.chain,
    activeCoin.symbol,
    recipientValid ? recipient : undefined,
    source?.eoaAddress,
  );
  const isNative = !!activeCoin.native;
  const isBtc = activeCoin.chain === 'btc';
  const amountNum = Number(amount) || 0;
  const btcSpend = useBtcSpendQuote(
    isBtc ? source?.eoaAddress : undefined,
    isBtc ? source?.publicKey : undefined,
    isBtc && amountNum > 0 ? amountNum : undefined,
    isBtc && recipientValid ? recipient : undefined,
  );
  // Live spot price for the ≈$ line (stables 1; natives from the balances feed).
  const price = priceBySymbol[activeCoin.symbol] ?? (isNative ? null : 1);
  const fee = isBtc
    ? (btcSpend.feeBtc ?? 0) * (price ?? 0)
    : (feeQuote ?? (isNative ? 0 : TRANSFER_FEE_USD));

  // Stablecoins: the fee is charged ON TOP in the same token. Natives: the
  // network fee comes out of the SAME native balance — either way the wallet
  // must cover amount + fee (fee measured in the coin being sent).
  // The fee depends on the recipient, so only evaluate (and show) it once a valid
  // recipient is entered — no premature "insufficient" based on a guessed fee.
  const feeInCoin = isBtc ? (btcSpend.feeBtc ?? 0) : isNative ? (feeNative ?? 0) : fee;
  // Gate on what the send will RESERVE, not on what we display. A first Tron send
  // holds headroom over the quote (its exact cost is only known after the one-time
  // approve lands), and offering an amount create then rejects is a dead end.
  const feeReserveInCoin = isBtc
    ? (btcSpend.feeBtc ?? 0)
    : isNative
      ? (feeNative ?? 0)
      : (maxFeeUsd ?? fee);
  const exceeds =
    recipientValid && Number(amount) + feeReserveInCoin > available && Number(amount) > 0;
  const submitting = sending || send.isPending;

  // Never let the user commit against a fee we haven't priced for THIS send.
  // Sponsored stablecoins need this as much as natives: until the quote lands,
  // `fee` is the flat placeholder, so a fresh Tron wallet (real fee ~$5) would
  // pass the balance check at ~$1.50 and then be refused by create.
  const feeReady = isBtc
    ? btcSpend.ready
    : feeFresh && (!isNative || feeNative != null);
  const canSend =
    Number(amount) > 0 &&
    recipientValid &&
    // A malformed tag must not be sendable: the ledger cannot tell a mistyped
    // tag from a real one and would credit whoever it names at the exchange.
    !memoInvalid &&
    !exceeds &&
    feeReady &&
    !!wallet?.id &&
    !submitting &&
    !sent;

  // Leave room for the fee when we already know it (same gate as `exceeds`).
  // BTC Max uses the same selectUTXO planner as the signed send.
  const maxSendable = isBtc
    ? Math.max(0, btcSpend.maxBtc ?? 0)
    : Math.max(0, available - (recipientValid ? feeReserveInCoin : 0));
  const fillMax = () => {
    const decimals = DISPLAY_DECIMALS[activeCoin.symbol] ?? 2;
    const factor = 10 ** decimals;
    const floored = Math.floor(maxSendable * factor + 1e-9) / factor;
    if (floored <= 0) {
      setAmount('0');
      return;
    }
    setAmount(floored.toFixed(decimals).replace(/\.?0+$/, ''));
  };

  const submit = async (reauthToken?: string) => {
    if (!wallet?.id) return;
    setError(null);
    setSending(true);
    if (!idempotencyRef.current) idempotencyRef.current = globalThis.crypto.randomUUID();
    try {
      // Let the progress pill paint before signing libraries do synchronous work.
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
      await send.mutateAsync({
        walletId: wallet.id,
        chain: activeCoin.chain,
        token: activeCoin.symbol,
        to: recipient,
        amount,
        reauthToken,
        fromAddressId: source?.id,
        fromIndex: source?.derivationIndex,
        ...(chainTakesMemo && memoInput !== '' ? { memo: memoInput } : {}),
        idempotencyKey: idempotencyRef.current,
      });
      setSent(true);
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err);
      // Keep the key while receipt reconciliation is still in flight; otherwise a
      // re-tap would create a second transfer and risk a double broadcast.
      if (!/transfer_verification_pending|transfer_preparing|uo_inclusion_timeout/i.test(raw)) {
        idempotencyRef.current = null;
      }
      dbg(`SendScreen ${activeCoin.chain}: ${raw.slice(0, 180)}`);
      setError(parseTransferError(raw, t));
    } finally {
      setSending(false);
    }
  };

  // From the confirm sheet: gate with reauth (PIN/biometric) when one is set.
  const onConfirmSend = () => {
    setConfirmOpen(false);
    if (needsReauth) setReauthOpen(true);
    else void submit();
  };

  // Warm the heavy signing bundle for this chain on mount, so the first send
  // doesn't pay its parse/eval cost on the critical path. tronweb (Tron) and the
  // Account Kit gasless client (ETH) are otherwise imported lazily at submit.
  useEffect(() => {
    if (activeCoin.chain === 'trx') void preloadTronWeb();
    else if (activeCoin.chain === 'btc') void import('@incpt/blockchain/btc');
    else if (activeCoin.native) void import('../../lib/nativeEth');
    else void import('../../lib/gasless');
  }, [activeCoin.chain, activeCoin.native]);

  // Keep the success state visible briefly, then return to the wallet home.
  useEffect(() => {
    if (!sent) return;
    const id = window.setTimeout(onClose, 5000);
    return () => window.clearTimeout(id);
  }, [sent, onClose]);

  // Auto-dismiss the error toast.
  useEffect(() => {
    if (!error) return;
    const id = window.setTimeout(() => setError(null), 3200);
    return () => window.clearTimeout(id);
  }, [error]);

  // Pull a bare address out of a scanned QR payload. Wallet QRs are often a
  // payment URI (e.g. "tron:TXYZ…?amount=1", "ethereum:0xabc…") — strip the
  // scheme and any query string so we keep just the address.
  const parseScannedAddress = (raw: string): string => {
    let s = raw.trim();
    const schemeMatch = s.match(/^[a-z][a-z0-9+.-]*:(.*)$/i);
    if (schemeMatch && !/^0x/i.test(s)) s = schemeMatch[1] ?? s;
    s = s.split(/[?&]/)[0] ?? s;
    s = s.trim();
    // BIP-21 QRs often carry ALL-CAPS bech32 (efficient QR mode) — normalize.
    if (/^BC1[A-Z0-9]+$/.test(s)) s = s.toLowerCase();
    return s;
  };

  const scan = () => {
    const wa = window.Telegram?.WebApp;
    // Native Telegram QR scanner (Bot API 6.4+). telegram-web-app.js also
    // exposes a no-op stub on the standalone site, so require real initData
    // (same guard as BackButton / CloudStorage).
    if (wa?.initData && wa.showScanQrPopup) {
      wa.showScanQrPopup({ text: t('send.scanHint') }, (text) => {
        const addr = parseScannedAddress(text);
        if (addr && isValidAddress(activeCoin.chain, addr)) {
          setAddress(addr);
          wa.closeScanQrPopup?.();
          return true;
        }
        // Keep scanning until a valid address for this network is found.
        return false;
      });
      return;
    }
    // Standalone web: open the in-app camera scanner.
    setScanOpen(true);
  };

  // Stable identity (functional updates, no deps) so the memoized Numpad doesn't
  // re-render on every keystroke.
  const maxDecimals = DISPLAY_DECIMALS[activeCoin.symbol] ?? 2;
  const handleKey = useCallback(
    (k: NumpadKey) => {
      setAmount((prev) => applyAmountKey(prev, k, maxDecimals));
    },
    [maxDecimals],
  );

  return (
    <div className="relative flex h-full min-h-full flex-col">
      <ScreenBackdrop full />
      <div className="relative z-10 flex h-full min-h-full flex-col px-4 pb-[max(1.25rem,var(--tg-safe-bottom))] pt-[calc(var(--tg-safe-top)_+_0.75rem)]">
        {/* Header: back + wallet selector */}
        <div className="relative flex h-10 items-center justify-center">
          {!tgBack && (
            <button
              onClick={onClose}
              className="absolute left-0 flex h-9 w-9 items-center justify-center rounded-full bg-black/[0.35] text-white"
              aria-label="Back"
            >
              <ChevronRight size={18} className="rotate-180 text-white" />
            </button>
          )}
          <button
            onClick={() => setWalletOpen(true)}
            className="flex items-center gap-1.5 rounded-full bg-white py-1.5 pl-1.5 pr-3 shadow-card"
          >
            <activeCoin.Icon size={24} />
            <span className="text-[14px] font-medium text-ink">{sourceName}</span>
            <ChevronRight size={16} className="rotate-90 text-muted" />
          </button>
        </div>

        {/* Amount — tap dismisses the address keyboard and brings the numpad back. */}
        <div
          className="mt-10 flex flex-col items-center gap-2 text-center"
          onPointerDown={() => {
            if (!addressFocusedRef.current) return;
            addressInputRef.current?.blur();
            clearAddressMode();
          }}
        >
          <div className="flex items-center gap-2">
            <p className="text-[14px] text-muted">
              {t('send.available')}{' '}
              <span className="font-medium text-ink">
                {formatTokenAmount(available, activeCoin.symbol)} {activeCoin.symbol}
              </span>
            </p>
            <button
              type="button"
              onClick={fillMax}
              disabled={maxSendable <= 0}
              className="rounded-full bg-[#eef2ff] px-2.5 py-0.5 text-[12px] font-semibold uppercase tracking-wide text-[#377dff] active:opacity-70 disabled:opacity-40"
            >
              {t('send.max')}
            </button>
          </div>
          <p className="text-[56px] font-medium leading-none tabular-nums">
            <span className={exceeds ? 'text-[#ff3b30]' : 'text-ink'}>
              {amount.slice(0, -1)}
              <span key={amount} className="pin-pop">
                {amount.slice(-1)}
              </span>
            </span>{' '}
            <span className="text-muted">{activeCoin.symbol}</span>
          </p>
          {exceeds ? (
            <p className="flex items-center gap-1.5 text-[14px] font-medium text-[#ff3b30]">
              <WarningIcon size={16} />
              {t('send.insufficient')}
            </p>
          ) : (
            <p className="text-[14px] text-muted">
              ≈ ${price != null ? formatUsd((Number(amount) || 0) * price) : '—'}
            </p>
          )}
          {recipientValid && (
            <p className="text-[13px] text-muted">
              {!feeReady
                ? t('send.feeLoading')
                : isNative
                  ? t('send.requiredFeeNative', {
                      amount: formatTokenAmount(feeInCoin, activeCoin.symbol),
                      symbol: activeCoin.symbol,
                      usd: fee.toFixed(2),
                    })
                  : maxFeeUsd != null && maxFeeUsd > fee + 0.001
                    ? t('send.requiredFeeReserve', {
                        amount: fee.toFixed(2),
                        reserve: maxFeeUsd.toFixed(2),
                      })
                    : t('send.requiredFee', { amount: fee.toFixed(2) })}
            </p>
          )}
          <NetworkSelect
            coins={altCoins}
            value={activeChain}
            onChange={(chain) => {
              setActiveChain(chain);
              setSelectedAddr(null);
              setAddress('');
              setAmount('0');
            }}
            className="mt-1 flex items-center gap-1 rounded-full bg-black/[0.06] py-1.5 pl-3 pr-2 active:opacity-70"
          />
        </div>

        <div className="flex-1" />

        {/* Spacer so the amount block doesn't jump when the dock goes fixed. */}
        {dockFlowHeight > 0 && (
          <div aria-hidden className="shrink-0" style={{ height: dockFlowHeight }} />
        )}

        <div
          ref={addressDockRef}
          className={
            dockTop != null
              ? 'fixed inset-x-4 z-20 flex flex-col'
              : 'mb-7 flex shrink-0 flex-col'
          }
          style={dockTop != null ? { top: dockTop } : undefined}
        >
          {/* iOS-only: the UA stylesheet puts border-radius:5px on <input>.
              That clips the first glyph when padding lives on a wrapper.
              Match TokenSheet — radius + pad on the input itself; scan sits
              outside so it is not an ancestor with overflow/radius. */}
          <div className="flex items-center gap-2">
            <input
              ref={addressInputRef}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              onFocus={() => {
                const dock = addressDockRef.current;
                const top = dock?.getBoundingClientRect().top ?? null;
                addressFocusedRef.current = true;
                nativeKeyboardSeenRef.current = false;
                // A refocus without an intervening blur would otherwise be
                // measured against the previous keyboard's peak.
                keyboardPeakRef.current = 0;
                setDockFlowHeight(dock?.offsetHeight ?? 0);
                setAddressFocused(true);
                setDockTop(top);
              }}
              onBlur={() => {
                clearAddressMode();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
              }}
              enterKeyHint="done"
              placeholder={t('send.recipientPlaceholder')}
              className={`min-w-0 flex-1 ${PILL_INPUT} ${
                badAddress ? 'ring-1 ring-[#ff3b30]' : ''
              }`}
            />
            <button
              onClick={scan}
              aria-label={t('send.scan')}
              className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white shadow-card active:opacity-70"
            >
              <ScanIcon size={16} className="text-[#377dff]" />
            </button>
          </div>
          {resolvingId && (
            <p className="mt-2 flex items-center gap-1.5 px-1 text-[13px] text-muted">
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-black/15 border-t-[#377dff]" />
              {t('send.resolvingId')}
            </p>
          )}
          {resolutionMatchesInput && recipientValid && (
            <p className="mt-2 px-1 text-[13px] font-medium text-positive">
              {t('send.idResolved', {
                id: telegramId,
                address: shortenAddress(recipient),
              })}
            </p>
          )}
          {idLookupFailed && (
            <p className="mt-2 flex items-center gap-1.5 px-1 text-[13px] font-medium text-[#ff3b30]">
              <WarningIcon size={15} />
              {resolved.noAddressForChain
                ? t('send.errNoAddressForChain', { network: activeCoin.network })
                : t('send.errUserNotFound')}
            </p>
          )}
          {badAddress && !idLookupFailed && (
            <p className="mt-2 flex items-center gap-1.5 px-1 text-[13px] font-medium text-[#ff3b30]">
              <WarningIcon size={15} />
              {t('send.invalidAddress', { network: activeCoin.network })}
            </p>
          )}

          {chainTakesMemo && !sent && (
            <div className="mt-2">
              <input
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.currentTarget.blur();
                }}
                enterKeyHint="done"
                inputMode="numeric"
                placeholder={t('send.memoPlaceholder')}
                className={`w-full ${PILL_INPUT} ${
                  memoInvalid ? 'ring-1 ring-[#ff3b30]' : ''
                }`}
              />
              <p
                className={`mt-2 flex items-center gap-1.5 px-1 text-[13px] ${
                  memoInvalid ? 'font-medium text-[#ff3b30]' : 'text-muted'
                }`}
              >
                {memoInvalid && <WarningIcon size={15} />}
                {memoInvalid ? t('send.memoInvalid') : t('send.memoHint')}
              </p>
            </div>
          )}

          {sent ? (
            <button
              type="button"
              onClick={onClose}
              className="mt-4 flex h-14 w-full items-center justify-center rounded-full bg-ink text-[15px] font-medium text-white active:opacity-90"
            >
              {t('common.done')}
            </button>
          ) : (
            <ProgressButton
              active={submitting}
              done={false}
              onClick={() => setConfirmOpen(true)}
              disabled={!canSend}
              className={`mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-ink text-[15px] font-medium text-white transition-opacity ${
                submitting
                  ? 'pointer-events-none'
                  : canSend
                    ? 'active:opacity-90'
                    : 'pointer-events-none opacity-40'
              }`}
            >
              {submitting ? (
                <>
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  {t('send.sending')}
                </>
              ) : (
                <>
                  <DiagonalArrow size={20} className="text-white" />
                  {t('send.submit')}
                </>
              )}
            </ProgressButton>
          )}
        </div>

        {!addressFocused && (
          <div className="mt-4 shrink-0">
            <Numpad onKey={handleKey} />
          </div>
        )}
      </div>

      {/* Source wallet / address selector */}
      <AddressSheet
        open={walletOpen}
        onClose={() => setWalletOpen(false)}
        coin={activeCoin}
        title={t('send.selectWallet')}
        onSelect={(addr) => {
          setSelectedAddr(addr);
          setWalletOpen(false);
        }}
      />

      {/* Confirmation */}
      <BottomSheet
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={t('send.confirmTitle')}
      >
        <div className="mt-5 flex flex-col">
          <Row label={t('send.amount')} value={`${amount} ${activeCoin.symbol}`} />
          <Row
            label={t('send.recipient')}
            value={
              telegramId
                ? `ID ${telegramId} · ${shortenAddress(recipient)}`
                : shortenAddress(recipient)
            }
          />
          {chainTakesMemo && memoInput !== '' && (
            // Shown unabbreviated and in full. This is the one field the user
            // copied from an exchange, the one the ledger cannot sanity-check,
            // and the one whose mistake is unrecoverable — so it gets a last
            // look before they commit, not a shortened preview.
            <Row label={t('send.memoLabel')} value={memoInput} />
          )}
          <Row
            label={t('send.fee')}
            value={
              isNative
                ? `≈ ${formatTokenAmount(feeInCoin, activeCoin.symbol)} ${activeCoin.symbol} ($${fee.toFixed(2)})`
                : maxFeeUsd != null && maxFeeUsd > fee + 0.001
                  ? t('send.feeReserveValue', {
                      amount: fee.toFixed(2),
                      reserve: maxFeeUsd.toFixed(2),
                    })
                  : `≈ $${fee.toFixed(2)}`
            }
          />
          <Row
            label={t('send.total')}
            value={`≈ ${formatTokenAmount(Number(amount) + feeInCoin, activeCoin.symbol)} ${activeCoin.symbol}`}
            accent
          />
        </div>
        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={() => setConfirmOpen(false)}
            className="h-14 flex-[2] rounded-full bg-[#f2f3f4] text-[15px] font-medium text-ink active:bg-black/[0.06]"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={onConfirmSend}
            disabled={submitting}
            className={`flex h-14 flex-[3] items-center justify-center gap-2 rounded-full bg-ink text-[15px] font-medium text-white ${
              submitting ? 'opacity-60' : 'active:opacity-90'
            }`}
          >
            {submitting ? t('send.sending') : t('send.submit')}
            {!submitting && <DiagonalArrow size={18} className="text-white" />}
          </button>
        </div>
      </BottomSheet>

      <QrScanOverlay
        open={scanOpen}
        hint={t('send.scanHint')}
        onClose={() => setScanOpen(false)}
        onError={setError}
        onScan={(text) => {
          const addr = parseScannedAddress(text);
          if (addr && isValidAddress(activeCoin.chain, addr)) {
            setAddress(addr);
            return true;
          }
          return false;
        }}
      />

      {/* Reauth before submitting (when a PIN/biometric is set) */}
      {reauthOpen && canFetch && (
        <div className="push-enter fixed inset-0 z-[60] overflow-y-auto bg-holo">
          <ReauthFlow
            initData={initData}
            biometricEnabled={!!me?.biometricEnabled}
            reason={t('reauth.reasonSend')}
            onSuccess={(token) => {
              setReauthOpen(false);
              void submit(token);
            }}
            onCancel={() => setReauthOpen(false)}
          />
        </div>
      )}

      {/* Error toast */}
      {error && (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex justify-center px-4 pt-[calc(var(--tg-safe-top)_+_1.25rem)]">
          <div className="animate-toast flex items-center gap-3 rounded-[20px] bg-ink px-5 py-4 text-white shadow-[0px_16px_40px_rgba(0,0,0,0.28)]">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#ff3b30]">
              <WarningIcon size={16} className="text-white" />
            </span>
            <span className="text-[14px] font-medium leading-tight">{error}</span>
          </div>
        </div>
      )}

      {sent && <Confetti />}
      <SendStatusPill
        visible={submitting || sent}
        sent={sent}
        amount={amount}
        coin={activeCoin}
        sendingLabel={t('send.sending')}
        sentLabel={t('send.sentTitle')}
      />
    </div>
  );
}

function SendStatusPill({
  visible,
  sent,
  amount,
  coin,
  sendingLabel,
  sentLabel,
}: {
  visible: boolean;
  sent: boolean;
  amount: string;
  coin: Coin;
  sendingLabel: string;
  sentLabel: string;
}) {
  if (!visible) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-[70] flex justify-center px-4 pt-[calc(var(--tg-safe-top)_+_6px)]">
      <div className="send-status-pill flex min-w-[168px] max-w-[280px] items-center gap-2.5 rounded-full bg-[#1A1B23] py-2 pl-2 pr-3 text-white shadow-[0_6px_14px_rgba(0,0,0,0.22)]">
        {sent ? (
          <span key="sent" className="send-status-success relative h-[38px] w-[38px] shrink-0">
            <span className="absolute inset-[3px] flex items-center justify-center rounded-full bg-[#34c759]">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12.5l4.5 4.5L19 7"
                  stroke="white"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
        ) : (
          <span className="relative flex h-[38px] w-[38px] shrink-0 items-center justify-center">
            <svg
              className="send-status-ring absolute inset-0"
              width="38"
              height="38"
              viewBox="0 0 38 38"
              fill="none"
              aria-hidden
            >
              <circle
                cx="19"
                cy="19"
                r="17.875"
                stroke="#4B9EFF"
                strokeWidth="2.25"
                strokeDasharray="31.45 80.86"
                strokeLinecap="round"
              />
            </svg>
            <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-[#2A2B33]">
              <coin.Icon size={32} />
            </span>
          </span>
        )}
        <div
          key={sent ? 'sent-copy' : 'sending-copy'}
          className="send-status-copy min-w-0 flex-1"
        >
          <div className="truncate text-[14px] font-semibold leading-tight tracking-[-0.1px]">
            {sent ? sentLabel : sendingLabel}
          </div>
          <div className="mt-px truncate text-[12px] font-medium leading-tight text-white/50">
            {amount} {coin.symbol}
          </div>
        </div>
        <ChevronRight size={16} className="shrink-0 text-white/45" />
      </div>
    </div>
  );
}

/** Map a backend error code (embedded in the thrown message) to a UI string.
 *  Never surface raw RPC / AA / Alchemy text — that goes to the ops log chat
 *  via PATCH failureReason. The toast is for the user only. */
function parseTransferError(message: string, t: (k: string) => string): string {
  const codes: Record<string, string> = {
    insufficient_balance: 'send.errInsufficient',
    below_min_amount: 'send.errMinAmount',
    daily_limit_reached: 'send.errDailyLimit',
    cooldown: 'send.errCooldown',
    transfer_in_progress: 'send.errCooldown',
    rate_limited: 'send.errCooldown',
    invalid_recipient: 'send.errInvalidRecipient',
    seed_unavailable: 'send.errSeedUnavailable',
    insufficient_bandwidth: 'send.errBandwidth',
    bandwidth_rental_failed: 'send.errBandwidth',
    energy_rental_failed: 'send.errNetworkBusy',
    // Sponsored sends refuse to price or run when the gas/price feed or the
    // sponsorship config is unavailable — retryable, not the user's fault.
    fee_estimation_unavailable: 'send.errNetworkBusy',
    eth_sponsorship_unavailable: 'send.errNetworkBusy',
    sponsor_risk_limit: 'send.errDailyLimit',
    // Self-paid sends: not enough coin for gas / not enough confirmed BTC.
    insufficient_native_gas: 'send.errGasFunds',
    insufficient_funds: 'send.errInsufficient',
    // A self-paid token send spends two balances: the token for the amount and
    // the chain's coin for the gas. Saying "network fee" when it is the token
    // that is short sends the user to top up the wrong one.
    insufficient_token_balance: 'send.errInsufficient',
    // Solana: more decimals typed than the token has. Defensive — the input
    // already limits them — and truncating silently would send less than the
    // user asked for, so the send refuses instead.
    amount_too_precise: 'send.errMinAmount',
    // Zcash: the network's branch id could not be read, so a send would be
    // signed against an id the chain has moved past and rejected wholesale.
    // Retryable — the read failed, not the wallet.
    zec_branch_id_unavailable: 'send.errNetworkBusy',
    zec_unavailable: 'send.errNetworkBusy',
    // Dogecoin: the provider could not supply the funding transactions a
    // legacy input has to be signed against. Retryable — a rate limit, not an
    // empty wallet, and this is the error that keeps the two apart.
    doge_utxos_unavailable: 'send.errNetworkBusy',
    // XRP, and neither of these is retryable — both need the user to change
    // something, so "network busy" would send them round a loop forever.
    //
    // The destination asks for a tag we cannot attach. Delivering without one
    // is how money goes missing at an exchange, so the send refuses first.
    xrp_destination_tag_required: 'send.errXrpTagRequired',
    // A first payment to an account that does not exist yet must carry at
    // least the base reserve, or the ledger rejects it outright.
    xrp_below_destination_reserve: 'send.errXrpMinActivation',
    // Defensive: the catalogue keeps unsendable chains out of this screen, so
    // reaching this means the two disagree. "Network busy" would be a lie the
    // user retries forever.
    send_unavailable: 'send.errSendUnavailable',
    chain_has_no_tokens: 'send.errSendUnavailable',
    submit_failed: 'send.errSubmit',
    transfer_failed: 'send.errGeneric',
    transfer_incomplete: 'send.errGeneric',
    uo_inclusion_timeout: 'send.errNetworkBusy',
  };
  for (const [code, key] of Object.entries(codes)) {
    if (message.includes(code)) return t(key);
  }
  return t('send.errGeneric');
}

function Row({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex items-center justify-between border-b border-black/[0.06] py-3 last:border-b-0">
      <span className="text-[14px] text-muted">{label}</span>
      <span
        className={`text-[15px] font-medium tabular-nums ${accent ? 'text-[#377dff]' : 'text-ink'}`}
      >
        {value}
      </span>
    </div>
  );
}
